'use client';

import { useState, useCallback } from 'react';

export interface ItemDetailDialogState {
  isOpen: boolean;
  currentItemId: number | null;
  currentItemType: 'tv' | 'movie' | null;
  itemsList: number[]; // IDs of items in current view for navigation
  currentIndex: number;
}

export function useItemDetailDialog() {
  const [state, setState] = useState<ItemDetailDialogState>({
    isOpen: false,
    currentItemId: null,
    currentItemType: null,
    itemsList: [],
    currentIndex: -1,
  });

  const openDialog = useCallback((
    itemId: number,
    itemType: 'tv' | 'movie',
    itemsList: number[] = []
  ) => {
    const currentIndex = itemsList.indexOf(itemId);
    setState({
      isOpen: true,
      currentItemId: itemId,
      currentItemType: itemType,
      itemsList,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
    });
  }, []);

  const closeDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const navigateNext = useCallback(() => {
    setState(prev => {
      if (prev.itemsList.length === 0 || prev.currentIndex >= prev.itemsList.length - 1) {
        return prev;
      }
      const newIndex = prev.currentIndex + 1;
      return {
        ...prev,
        currentIndex: newIndex,
        currentItemId: prev.itemsList[newIndex],
      };
    });
  }, []);

  const navigatePrev = useCallback(() => {
    setState(prev => {
      if (prev.itemsList.length === 0 || prev.currentIndex <= 0) {
        return prev;
      }
      const newIndex = prev.currentIndex - 1;
      return {
        ...prev,
        currentIndex: newIndex,
        currentItemId: prev.itemsList[newIndex],
      };
    });
  }, []);

  const canNavigateNext = state.itemsList.length > 0 && state.currentIndex < state.itemsList.length - 1;
  const canNavigatePrev = state.itemsList.length > 0 && state.currentIndex > 0;

  return {
    ...state,
    openDialog,
    closeDialog,
    navigateNext,
    navigatePrev,
    canNavigateNext,
    canNavigatePrev,
  };
}
